import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  controlDeviceAccStatus,
  controlSoundWarning
} from 'src/features/deviceSlice';
import { controlMaximumSpeed } from 'src/features/safeZoneSlice';

// Âm thanh cảnh báo / tự động giới hạn tốc độ / ACC:
// gạt switch -> mở ModalConfirm -> xác nhận mới gọi API.
export default function useSystemSettings(d, notifySaved) {
  const dispatch = useDispatch();

  const [configAlert, setConfigAlert] = useState(d.is_turn_on_sound);
  const [configAutoUpdateMaxSpeed, setConfigAutoUpdateMaxSpeed] = useState(
    d.is_auto_update_maximum_speed
  );
  const [accStatus, setAccStatus] = useState(d.acc_status);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'sound' | 'autoSpeed' | 'acc'
  const [nextValue, setNextValue] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setConfigAlert(d.is_turn_on_sound);
    setConfigAutoUpdateMaxSpeed(d.is_auto_update_maximum_speed);
    setAccStatus(Boolean(d.acc_status));
  };

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.id]);

  const requestToggle = type => e => {
    if (loading) return;
    setNextValue(e.target.checked);
    setConfirmType(type);
    setOpenConfirm(true);
  };

  const runConfirmed = async (action, applyValue) => {
    try {
      setLoading(true);
      await dispatch(action).unwrap();
      applyValue(nextValue);
      notifySaved();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setOpenConfirm(false);
      setConfirmType(null);
    }
  };

  const confirmActions = {
    sound: {
      content: configAlert
        ? 'Bạn chỉ nên tắt âm thanh cảnh báo trong quá trình thử nghiệm! Bạn có chắc chắn muốn tắt âm thanh không?'
        : 'Bạn có chắc chắn muốn bật âm thanh cảnh báo không?',
      onOk: () =>
        runConfirmed(
          controlSoundWarning({
            id: d.id,
            data: { status: nextValue ? 0 : 1 }
          }),
          setConfigAlert
        )
    },
    autoSpeed: {
      content: configAutoUpdateMaxSpeed
        ? 'Bạn có chắc chắn muốn tắt tự động giới hạn tốc độ không?'
        : 'Bạn có chắc chắn muốn bật tự động giới hạn tốc độ không?',
      onOk: () =>
        runConfirmed(
          controlMaximumSpeed({
            id: d.license_plate,
            data: { enabled: nextValue }
          }),
          setConfigAutoUpdateMaxSpeed
        )
    },
    acc: {
      content: accStatus
        ? 'Bạn có chắc chắn muốn tắt ACC không?'
        : 'Bạn có chắc chắn muốn bật ACC không?',
      onOk: () =>
        runConfirmed(
          controlDeviceAccStatus({
            device_id: d.id,
            data: { status: nextValue ? 1 : 0 }
          }),
          setAccStatus
        )
    }
  };
  const activeConfirm = confirmActions[confirmType] || confirmActions.sound;

  return {
    configAlert,
    configAutoUpdateMaxSpeed,
    accStatus,
    loading,
    onToggleSound: requestToggle('sound'),
    onToggleAutoUpdateMaxSpeed: requestToggle('autoSpeed'),
    onToggleAccStatus: requestToggle('acc'),
    reset,
    confirmDialogProps: {
      title: 'Chú ý',
      content: activeConfirm.content,
      textOk: 'Đồng ý',
      textCancel: 'Hủy',
      open: openConfirm,
      loading,
      setOpen: value => {
        if (!loading) setOpenConfirm(value);
      },
      callbackOk: activeConfirm.onOk
    }
  };
}
